import { inject, Injectable, signal } from '@angular/core';
import { DOCUMENT } from '@angular/common';

export type Theme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'sh-theme';
export const DARK_STYLESHEET_HREF = 'assets/themes/dark.css';
export const THEME_SWITCHING_CLASS = 'sh-theme-switching';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly document = inject(DOCUMENT);
  private readonly _theme = signal<Theme>(ThemeService.readStored());

  /**
   * Read-only view of the theme for `computed()` / `effect()` consumers.
   * The `theme` getter below stays for callers that just want the value.
   */
  public readonly themeSignal = this._theme.asReadonly();

  private darkLink: HTMLLinkElement | null = null;

  public get theme(): Theme {
    return this._theme();
  }

  /**
   * Creates the dark-theme <link> and applies the stored theme.
   * Called from provideAppInitializer — before Angular injects component
   * styles, so their ::ng-deep overrides stay on top of the cascade.
   */
  public init(): Promise<void> {
    const isDark = this._theme() === 'dark';
    this.applyClass(isDark);

    const link = this.document.createElement('link');
    link.rel = 'stylesheet';
    link.href = DARK_STYLESHEET_HREF;
    link.media = isDark ? 'all' : 'not all';
    link.dataset.theme = 'dark';
    this.darkLink = link;

    const ready = new Promise<void>((resolve) => {
      link.addEventListener('load', () => resolve(), { once: true });
      link.addEventListener('error', () => resolve(), { once: true });
    });

    this.document.head.appendChild(link);

    return isDark ? ready : Promise.resolve();
  }

  public toggle(): void {
    this.set(this._theme() === 'dark' ? 'light' : 'dark');
  }

  public set(theme: Theme): void {
    const isDark = theme === 'dark';

    this._theme.set(theme);
    this.suppressTransitions();
    this.applyClass(isDark);

    if (this.darkLink) {
      this.darkLink.media = isDark ? 'all' : 'not all';
    }

    localStorage.setItem(THEME_STORAGE_KEY, theme);
  }

  private applyClass(isDark: boolean): void {
    this.document.documentElement.classList.toggle('dark', isDark);
  }

  /**
   * Disables CSS transitions while the theme flips, so every element recolors in
   * the same frame instead of staggering. The class is removed after two frames:
   * one for the new colors to paint, one to re-enable transitions.
   */
  private suppressTransitions(): void {
    const root = this.document.documentElement;
    root.classList.add(THEME_SWITCHING_CLASS);

    const raf = this.document.defaultView?.requestAnimationFrame;
    if (!raf) {
      root.classList.remove(THEME_SWITCHING_CLASS);
      return;
    }

    raf(() => raf(() => root.classList.remove(THEME_SWITCHING_CLASS)));
  }

  private static readStored(): Theme {
    return localStorage.getItem(THEME_STORAGE_KEY) === 'dark' ? 'dark' : 'light';
  }
}
